import { FloatingPanel } from '@hue/design-system';
import { Check, ChevronDown, Clock3, Search, X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  filterTimeZones,
  formatTime,
  getTimeZoneOptions,
  type TimeZoneOption,
} from '../lib/time-zone.js';

export function TimeZoneSelect({
  label,
  value,
  onValueChange,
  error,
  disabled,
  showTechnicalDetails = false,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  error?: string | undefined;
  disabled?: boolean | undefined;
  showTechnicalDetails?: boolean | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fieldRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const id = useId();
  const options = getTimeZoneOptions();
  const visibleOptions = filterTimeZones(options, search);
  const selected = options.find((option) => option.value === value) ?? fallbackOption(value);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  function select(option: TimeZoneOption) {
    onValueChange(option.value);
    setSearch('');
    setOpen(false);
  }

  return (
    <div className="space-y-2">
      <label id={`${id}-label`} className="block text-xs font-semibold text-content/70">
        {label}
      </label>
      <div className="relative" ref={fieldRef}>
        <button
          type="button"
          disabled={disabled}
          aria-labelledby={`${id}-label ${id}-value`}
          aria-controls={`${id}-options`}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((current) => !current)}
          className="flex min-h-12 w-full items-center gap-3 rounded-xl border border-surface-border bg-surface-elevated px-4 text-left transition hover:border-brand-500/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:opacity-50"
        >
          <Clock3 className="size-4 shrink-0 text-brand-400" />
          <span id={`${id}-value`} className="min-w-0 flex-1">
            <strong className="block truncate text-sm font-medium">{selected.city}</strong>
            <span className="block truncate font-mono text-[10px] text-content-muted">
              {showTechnicalDetails ? `${selected.value} · ` : ''}
              {selected.offset}
            </span>
          </span>
          <ChevronDown className={`size-4 transition ${open ? 'rotate-180' : ''}`} />
        </button>

        {open ? (
          <FloatingPanel
            anchorRef={fieldRef}
            onDismiss={close}
            width="anchor"
            className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
          >
            <div className="border-b border-surface-border p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-faint" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setOpen(false);
                  }}
                  aria-label="Buscar fuso horário"
                  placeholder="Cidade, código ou UTC…"
                  className="h-11 w-full rounded-xl border border-surface-border bg-surface-elevated pl-10 pr-10 text-sm outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Limpar busca de fuso"
                    className="absolute right-1 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-lg text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>
            </div>
            <div id={`${id}-options`} role="listbox" className="max-h-64 overflow-y-auto p-2">
              {visibleOptions.length ? (
                visibleOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => select(option)}
                    className="flex min-h-12 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                  >
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-xs font-semibold">
                        {option.city}
                      </strong>
                      <span className="block truncate font-mono text-[10px] text-content-muted">
                        {showTechnicalDetails ? `${option.value} · ` : ''}
                        {option.offset}
                      </span>
                    </span>
                    {option.value === value ? (
                      <Check className="size-4 shrink-0 text-brand-400" />
                    ) : null}
                  </button>
                ))
              ) : (
                <p className="px-3 py-6 text-center text-xs text-content-muted">
                  Nenhum fuso encontrado.
                </p>
              )}
            </div>
          </FloatingPanel>
        ) : null}
      </div>
      <p className="text-[11px] text-content-muted">
        Agora:{' '}
        <strong className="font-mono text-content-secondary">
          {formatTime(new Date(), value)}
        </strong>
      </p>
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function fallbackOption(value: string): TimeZoneOption {
  return { value, city: value, offset: '', search: value.toLocaleLowerCase('pt-BR') };
}
